// "Did you mean jane@gmail.com?" — twin of App\Support\Mail\EmailTypos,
// run as somebody types (and offline, in the field app). The two must
// agree address for address; harness/emailcheck_verify.php runs both
// over the same list. Change the lists in both or in neither.

export const TARGETS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
    'aol.com', 'comcast.net', 'verizon.net', 'sbcglobal.net', 'bellsouth.net',
    'protonmail.com', 'live.com', 'msn.com', 'att.net', 'charter.net',
];

export const REAL = [
    'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'rocketmail.com',
    'hotmail.com', 'outlook.com', 'live.com', 'msn.com', 'icloud.com', 'me.com',
    'mac.com', 'aol.com', 'aim.com', 'comcast.net', 'verizon.net', 'att.net',
    'sbcglobal.net', 'bellsouth.net', 'charter.net', 'cox.net', 'protonmail.com',
    'proton.me', 'pm.me', 'protonmail.ch', 'proton.ch', 'mail.com', 'email.com', 'gmx.com', 'gmx.net', 'hey.com',
    'fastmail.com', 'earthlink.net', 'frontier.com', 'optonline.net',
    'roadrunner.com', 'juno.com', 'windstream.net', 'yandex.com', 'zoho.com',
    'live.ca', 'yahoo.ca', 'hotmail.ca', 'outlook.ca', 'mail.ru', 'web.de',
    'gmail.co.uk', 'yahoo.co.uk', 'hotmail.co.uk', 'live.co.uk',
];

export const TLD_FIXES = {
    con: 'com', cmo: 'com', comm: 'com', coom: 'com',
    ocm: 'com', vom: 'com', xom: 'com', cpm: 'com',
    cim: 'com', conm: 'com', comn: 'com', clm: 'com',
    nett: 'net', nte: 'net', ner: 'net',
    ogr: 'org', orgg: 'org', prg: 'org', otg: 'org',
};

/** The corrected address, or null when nothing looks wrong. */
export function suggestEmail(email) {
    const e = String(email ?? '').trim().toLowerCase();
    const at = e.lastIndexOf('@');
    if (at <= 0) return null;
    const local = e.slice(0, at);
    const domain = e.slice(at + 1).replace(/\.+$/, '');
    if (domain === '') return null;
    const fixed = fixDomain(domain);
    return fixed !== null && fixed !== domain ? `${local}@${fixed}` : null;
}

function fixDomain(input) {
    let domain = input;
    if (REAL.includes(domain)) return null;

    if (! domain.includes('.')) {
        for (const t of TARGETS) if (t.replace(/\./g, '') === domain) return t;
        return null;
    }

    const dot = domain.lastIndexOf('.');
    const tld = domain.slice(dot + 1);
    if (Object.prototype.hasOwnProperty.call(TLD_FIXES, tld)) {
        domain = domain.slice(0, dot + 1) + TLD_FIXES[tld];
        if (REAL.includes(domain)) return domain;
    }

    let best = null;
    let bestDistance = Infinity;
    for (const t of TARGETS) {
        const limit = t.length >= 11 ? 2 : (t.length >= 9 ? 1 : 0);
        const d = distance(domain, t);
        if (d <= limit && d < bestDistance) { best = t; bestDistance = d; }
    }
    if (best !== null) return best;

    const name = domain.slice(0, domain.indexOf('.'));
    const rest = domain.slice(name.length + 1);
    if (['co', 'cm', 'om', 'c', 'cn'].includes(rest)) {
        for (const t of TARGETS) if (t.startsWith(name + '.') && t.endsWith('.com')) return t;
    }

    return domain;
}

/** Edit distance counting a swap of two neighbours as one slip. */
export function distance(a, b) {
    const n = a.length;
    const m = b.length;
    if (Math.abs(n - m) > 2) return 3;
    const d = [];
    for (let i = 0; i <= n; i++) d[i] = [i];
    for (let j = 0; j <= m; j++) d[0][j] = j;
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
            }
        }
    }
    return d[n][m];
}
